import {
  SET_ALBUM_FOLDER,
  SELECT_IDENTITY,
  DESELECT_IDENTITY,
  RESET_IDENTITY,
  SELECT_ALBUM,
  DESELECT_ALBUM,
  JOIN_ALBUMS_REQUEST,
  JOIN_ALBUMS_SUCCESS,
  JOIN_ALBUMS_FAILURE,
  RESET_ALBUM_DISCOVERY,
  PURCHASE_ALBUMS_REQUEST,
  PURCHASE_ALBUMS_SUCCESS,
  PURCHASE_ALBUMS_FAILURE,
  CHECKOUT_REQUEST,
  CHECKOUT_SUCCESS,
  CHECKOUT_FAILURE,
  SET_JOIN_TYPE_DEFAULT,
  SET_JOIN_TYPE_SELECTION_FLAG,
  SET_DISCOVERY_PRESELECTION_VISIBILITY,
  SELECT_CUSTOM_PRINT,
  DESELECT_CUSTOM_PRINT,
} from '../actions/discover-album-folder';
import { PPGD_JOIN_TYPE } from '../constants/discover-album-folder';

const initialState = {
  albumFolderId: null, // starting (landing) album folder
  currentIdentityId: null, // selected identity
  identities: [], // all identity selections
  loading: false, // join/purchase albums in-progress
  error: '', // join/purchase albums error
  success: false, // join/purchase albums success
  purchasedSubscription: undefined,
  joinTypeDefault: PPGD_JOIN_TYPE.JOIN,
  allowsJoinTypeSelection: false, // Whether the user can choose between join / purchase - determined by the album folder
  hasPreselectedJoinType: false, // Whether the user has already pre-selected a join type during the first steps of onboarding (i.e. some kind of intro screen).
};

const discoverAlbumFolderReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case SET_ALBUM_FOLDER:
      return { ...state, albumFolderId: action.albumFolderId };

    case SELECT_IDENTITY: {
      const identityExists = !!state.identities.find(
        identity => identity.id === action.identity.id,
      );

      if (identityExists) {
        return {
          ...state,
          currentIdentityId: action.identity.id,
          identities: state.identities.map(identity =>
            identity.id === action.identity.id
              ? {
                  ...action.identity,
                  // If there was a pre-configured selected album set, we need to keep it.
                  selectedAlbums:
                    identity.selectedAlbums || action.identity.selectedAlbums,
                  joinType: action.joinType || state.joinTypeDefault,
                }
              : identity,
          ),
        };
      }

      return {
        ...state,
        currentIdentityId: action.identity.id,
        identities: [
          ...state.identities,
          {
            ...action.identity,
            joinType: action.joinType || state.joinTypeDefault,
          },
        ],
      };
    }

    case DESELECT_IDENTITY: {
      return {
        ...state,
        currentIdentityId:
          action.identity.id === state.currentIdentityId
            ? null
            : state.currentIdentityId,
        identities: state.identities.filter(
          identity => identity.id !== action.identity.id,
        ),
      };
    }

    case RESET_IDENTITY:
      return { ...state, currentIdentityId: null };

    case SELECT_ALBUM:
      return {
        ...state,
        identities: state.identities.map(identity => {
          if (identity.id !== state.currentIdentityId) {
            return identity;
          }

          return {
            ...identity,
            selectedAlbums: [...identity.selectedAlbums, action.album],
          };
        }),
      };

    case DESELECT_ALBUM: {
      const identityId = action.identityId || state.currentIdentityId;

      return {
        ...state,
        identities: state.identities.map(identity => {
          if (identity.id !== identityId) {
            return identity;
          }

          const { album } = action;

          return {
            ...identity,
            selectedAlbums: identity.selectedAlbums.filter(
              selectedAlbum => selectedAlbum.id !== album.id,
            ),
            // Remove all album custom prints that belong to the removed album
            selectedAlbumCustomPrints: (
              identity?.selectedAlbumCustomPrints ?? []
            ).filter(
              albumCustomPrint => albumCustomPrint.album.id !== album.id,
            ),
          };
        }),
      };
    }

    case SELECT_CUSTOM_PRINT: {
      const identityId = action.identityId || state.currentIdentityId;

      return {
        ...state,
        identities: state.identities.map(identity => {
          if (identity.id !== identityId) {
            return identity;
          }

          const { albumCustomPrint } = action;
          const { selectedAlbumCustomPrints = [] } = identity;

          // Make sure if we find the same custom print we just replace it
          // instead of adding it a second time.
          const existingAlbumCustomPrint = selectedAlbumCustomPrints.find(
            ({ id }) => id === albumCustomPrint.id,
          );

          if (existingAlbumCustomPrint) {
            return {
              ...identity,
              selectedAlbumCustomPrints: selectedAlbumCustomPrints.map(
                selectedAlbumCustomPrint => {
                  if (selectedAlbumCustomPrint.id === albumCustomPrint.id) {
                    return albumCustomPrint;
                  }

                  return selectedAlbumCustomPrint;
                },
              ),
            };
          }

          return {
            ...identity,
            selectedAlbumCustomPrints: [
              ...selectedAlbumCustomPrints,
              action.albumCustomPrint,
            ],
          };
        }),
      };
    }

    case DESELECT_CUSTOM_PRINT: {
      const identityId = action.identityId || state.currentIdentityId;

      return {
        ...state,
        identities: state.identities.map(identity => {
          if (identity.id !== identityId) {
            return identity;
          }

          return {
            ...identity,
            selectedAlbumCustomPrints: (
              identity?.selectedAlbumCustomPrints ?? []
            ).filter(
              albumCustomPrint =>
                albumCustomPrint.id !== action.albumCustomPrint.id,
            ),
          };
        }),
      };
    }

    case JOIN_ALBUMS_REQUEST:
    case PURCHASE_ALBUMS_REQUEST:
    case CHECKOUT_REQUEST:
      return { ...state, loading: true, error: '', success: false };

    case JOIN_ALBUMS_SUCCESS:
    case PURCHASE_ALBUMS_SUCCESS:
    case CHECKOUT_SUCCESS:
      return {
        ...state,
        loading: false,
        error: '',
        success: true,

        // If checkout.is_paid is true (checkout mutation payload)
        // in this action it means the user purchased a subscription.
        // Since this is onboarding, you can either purchase a subscription
        // or join something for free (in which case `payload` is undefined).
        purchasedSubscription: !!action?.payload?.is_paid,
      };

    case JOIN_ALBUMS_FAILURE:
    case PURCHASE_ALBUMS_FAILURE:
    case CHECKOUT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error.message || action.error,
        success: false,
      };

    case SET_JOIN_TYPE_DEFAULT:
      return {
        ...state,
        joinTypeDefault: action.joinType,
      };

    case SET_JOIN_TYPE_SELECTION_FLAG:
      return {
        ...state,
        allowsJoinTypeSelection: action.flag,
      };

    case SET_DISCOVERY_PRESELECTION_VISIBILITY:
      return {
        ...state,
        hasPreselectedJoinType: action.flag,
      };

    case RESET_ALBUM_DISCOVERY:
      return {
        ...initialState,
        // Preserves `purchasedSubscription` as it's needed post-checkout.
        purchasedSubscription: state.purchasedSubscription,
      };

    default:
      return state;
  }
};

export { initialState };
export default discoverAlbumFolderReducer;
